import { AuthSection } from 'components/templates/templates-wizard/vcs/common/auth-section';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { SubFormProps } from 'components/common/form-controlled/with-controller.hoc';
import { FieldRow, Label } from 'components/common/form-components';
import ControlledTextInput from 'components/common/form-controlled/controlled-text-input';
import ControlledSelect from 'components/common/form-controlled/controlled-select';
import { SelectOption } from 'components/common/input-components/select';
import TokenSelectField from 'components/templates/templates-wizard/vcs/token-select-field';
import type { GitProviderTypes, Tokens } from 'types/api.types';
import type { BlueprintApi } from '@env0/blueprint-service/api';
import {
  providersSupportingOldRepositoryIntegration,
  providersSupportingVcsRepositoryIntegration,
  providerSupportingOauthIntegration
} from 'components/templates/templates-wizard/vcs/use-git-provider.hook';
import { observer } from 'mobx-react';
import Loader from 'components/common/loader';
import type { VCSForm } from 'components/templates/templates-wizard/common/template-wizard.types';
import { isGitProviderUsingTokenAndTokenIsMissing } from 'utils/vcs.utils';
import { VcsIntegrationMessage } from 'components/templates/templates-wizard/vcs/common/vcs-integration-message';
import { LocationSection } from 'components/templates/templates-wizard/vcs/common/location-section';
import Divider from 'components/common/divider';
import styled from '@emotion/styled';
import useAuthTokens from './use-auth-tokens.hook';
import BlockRadioButtons from 'components/common/block-radio-buttons';
import type { VcsApi } from '@env0/vcs-service/api';
import VcsRepositoryOauthSelector from 'components/templates/templates-wizard/vcs/vcs-repository-oauth-selector';
import VcsRepositorySelfHostedSelector from 'components/templates/templates-wizard/vcs/vcs-repository-self-hosted-selector';

export type TemplateWizardStepVCSFieldsProps = SubFormProps<VCSForm> & {
  gitProvider: GitProviderTypes;
  startVcsIntegration: (provider: GitProviderTypes) => void;
  isLoadingRepositories: boolean;
  repositories: BlueprintApi.GetAvailableRepositories.Response.Body;
  templateType: BlueprintApi.BlueprintType;
  isNewTemplate?: boolean;
  vcsRepositoryDisabled?: boolean;
  vcsRevisionDisabled?: boolean;
  showLocationSection?: boolean;
};

const TemplateWizardStepVCSFields: React.FunctionComponent<TemplateWizardStepVCSFieldsProps> = ({
  form,
  gitProvider,
  startVcsIntegration,
  repositories,
  isLoadingRepositories,
  templateType: rawTemplateType,
  isNewTemplate,
  vcsRepositoryDisabled,
  vcsRevisionDisabled,
  showLocationSection = true
}) => {
  const { setValue, trigger, clearErrors } = form;
  const intl = useIntl();
  const templateType = intl.formatMessage({ id: rawTemplateType || 'opentofu' });
  const selectRepositoryFromDropdown = providersSupportingOldRepositoryIntegration.has(gitProvider);
  const selectRepositoryFromVcsDropdown = providersSupportingVcsRepositoryIntegration.has(gitProvider);
  const showRevisionsDropdown = ['GitHub', 'GitLab', 'BitBucket', 'AzureDevOps'].includes(gitProvider);
  const showOauthSelector = providerSupportingOauthIntegration.has(gitProvider);
  const repoMessageId = useMemo(() => `templates.add.new.field.repository.${gitProvider.toLowerCase()}`, [gitProvider]);

  const { tokenId, repository } = form.watch();

  const { tokens, authType, onChangeAuthType, authTypeOptions, isLoadingTokens } = useAuthTokens(form, gitProvider);

  const initVSCData = useCallback(
    (newRepository: string) => {
      const currentRepository = repositories.find(repo => repo.httpsUrl === newRepository);
      if (!currentRepository) return;

      if (gitProvider === 'BitBucket') {
        setValue('bitbucketClientKey', currentRepository.bitbucket?.clientKey || null);
      }
    },
    [gitProvider, repositories, setValue]
  );

  // init VSC data from repositories - Part of them required for update
  useEffect(() => {
    if (!isNewTemplate) {
      initVSCData(repository);
    }
  }, [isNewTemplate, initVSCData, repository]);

  useEffect(() => {
    if (rawTemplateType !== 'cloudformation') {
      setValue('fileName', '');
    }
    trigger('fileName').then(() => {
      clearErrors('fileName');
    });
  }, [setValue, trigger, clearErrors, rawTemplateType]);

  const tokenField = (provider: GitProviderTypes, tokens: Tokens) => {
    const lowerCaseProvider = `${provider.toLowerCase()}`;
    return (
      <TokenSelectField
        width={49}
        label={{ id: `templates.add.new.field.authorize.as.${lowerCaseProvider}` }}
        mandatory
        info={{ id: `templates.add.new.field.authorize.as.${lowerCaseProvider}.info` }}
        data-e2e={`${lowerCaseProvider}-token-field`}
        form={form}
        gitProvider={provider}
        tokens={tokens}
      />
    );
  };

  const gitlabTokenField = tokenField('GitLab', tokens!);

  const gitLabAccessTokenSelectField = tokenField('Other', tokens!);

  const azureDevOpsTokenField = tokenField('AzureDevOps', tokens!);

  const repositoryLabelProps = {
    inline: true,
    width: 50,
    label: { id: repoMessageId },
    mandatory: true,
    info: { id: `${repoMessageId}.info`, values: { templateType } }
  };

  if (isLoadingTokens) {
    return <Loader />;
  }

  const repositoryTooltip = vcsRepositoryDisabled
    ? { placement: 'top', titleId: 'templates.add.new.field.repository.disabled' }
    : undefined;

  const renderRepositoryField = () => {
    // Old integration with dropdown
    if (selectRepositoryFromDropdown) {
      return (
        <ControlledSelect
          {...repositoryLabelProps}
          showSearch
          form={form}
          name="repository"
          data-e2e="repository-select"
          loading={isLoadingRepositories}
          disabled={vcsRepositoryDisabled || isGitProviderUsingTokenAndTokenIsMissing(gitProvider, tokenId)}
          tooltip={repositoryTooltip}
          onChange={initVSCData}
          placeholder={<FormattedMessage id={`${repoMessageId}.placeholder`} />}
          customNotFoundContent={<FormattedMessage id="templates.add.new.field.repository.empty" />}>
          {repositories.map(repository => (
            <SelectOption
              data-e2e={`repository-option-${repository.name}`}
              key={`repository-option-${repository.name}`}
              value={repository.httpsUrl}>
              {repository.name}
            </SelectOption>
          ))}
        </ControlledSelect>
      );
    }

    if (selectRepositoryFromVcsDropdown) {
      const VcsSelector = showOauthSelector ? VcsRepositoryOauthSelector : VcsRepositorySelfHostedSelector;

      return <VcsSelector {...repositoryLabelProps} vcsType={gitProvider as VcsApi.VcsType} form={form} />;
    }

    return (
      <ControlledTextInput
        {...repositoryLabelProps}
        form={form}
        id="add-new-template-repository-input"
        name="repository"
        data-e2e="repository-url-field"
        placeholder={`templates.add.new.field.repository.${
          gitProvider === 'HelmRepository' ? 'helmRepo' : 'other'
        }.placeholder`}
        disabled={vcsRepositoryDisabled}
        tooltip={repositoryTooltip}
      />
    );
  };

  return (
    <Container>
      {gitProvider === 'GitLab' && (
        <BlockRadioButtons
          onChange={onChangeAuthType}
          value={authType}
          options={authTypeOptions}
          label={<Label id={'templates.add.new.field.authorize.type'} />}
        />
      )}
      {gitProvider === 'GitLab' && authType === 'GitLab' && gitlabTokenField}
      {gitProvider === 'GitLab' && authType === 'GIT' && gitLabAccessTokenSelectField}
      {gitProvider === 'AzureDevOps' && azureDevOpsTokenField}
      <FieldRow zeroBottomMargin>{renderRepositoryField()}</FieldRow>
      {selectRepositoryFromDropdown && (
        <VcsIntegrationMessage gitProvider={gitProvider} onClick={() => startVcsIntegration(gitProvider)} />
      )}
      {showLocationSection && (
        <LocationSection
          form={form}
          rawTemplateType={rawTemplateType}
          gitProvider={gitProvider}
          vcsRevisionDisabled={vcsRevisionDisabled}
          showRevisionsDropdown={showRevisionsDropdown}
          templateType={templateType as BlueprintApi.BlueprintType}
        />
      )}
      {rawTemplateType === 'module' && (
        <div data-e2e={`module-tag-prefix-section`}>
          <Divider data-e2e={`module-tag-prefix-divider`} titleId="templates.add.new.divider.tag-prefix" />
          <FieldRow>
            {
              <ControlledTextInput
                inline={true}
                width={50}
                label={{ id: 'templates.add.new.field.repository.tag-prefix' }}
                info={{ id: 'templates.add.new.field.repository.tag-prefix.info' }}
                form={form}
                id="add-new-template-repository-tag-prefix-input"
                name="tagPrefix"
                data-e2e="repository-tag-prefix-field"
              />
            }
          </FieldRow>
        </div>
      )}
      {gitProvider === 'Other' && rawTemplateType !== 'helm' && (
        <AuthSection form={form} gitProvider={gitProvider} tokens={tokens!} />
      )}
    </Container>
  );
};

const Container = styled.div`
  margin-top: 1em;
`;

export default observer(TemplateWizardStepVCSFields);
